import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, CircleAlert, CircleHelp, X } from 'lucide-react';
import { GoHoverTooltip } from './GoHoverTooltip';
import './GoDeviceInstallStatus.css';

function formatWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return null;
  }
}

function statusMeta(status) {
  if (status === 'ok') {
    return { label: 'OK', tip: 'Instalação ok', className: 'is-ok' };
  }
  if (status === 'attention') {
    return { label: 'Atenção', tip: 'Há pontos a ajustar no aparelho', className: 'is-attention' };
  }
  return { label: '—', tip: 'Ainda sem relatório do app', className: 'is-unknown' };
}

function LevelIcon({ level }) {
  if (level === 'ok') return <Check size={14} strokeWidth={2.4} aria-hidden />;
  if (level === 'bad') return <CircleAlert size={14} strokeWidth={2} aria-hidden />;
  return <CircleHelp size={14} strokeWidth={2} aria-hidden />;
}

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

/**
 * Avatar do modal: skeleton enquanto a foto S3 (proxy) carrega; fallback em iniciais.
 */
function InstallModalAvatar({ name, photoUrl }) {
  const initials = initialsFromName(name);
  const hasPhoto = Boolean(photoUrl);
  const [phase, setPhase] = useState(() => (hasPhoto ? 'loading' : 'fallback'));

  useEffect(() => {
    setPhase(photoUrl ? 'loading' : 'fallback');
  }, [photoUrl]);

  if (!hasPhoto || phase === 'fallback') {
    return (
      <div className="go-dev-install-avatar go-dev-install-avatar--fallback" aria-hidden>
        {initials}
      </div>
    );
  }

  return (
    <div className="go-dev-install-avatar" aria-hidden>
      {phase === 'loading' ? <span className="go-dev-install-avatar-skel" /> : null}
      <img
        src={photoUrl}
        alt=""
        className={`go-dev-install-avatar-img${phase === 'ready' ? ' is-ready' : ''}`}
        referrerPolicy="no-referrer"
        decoding="async"
        onLoad={() => setPhase('ready')}
        onError={() => setPhase('fallback')}
      />
    </div>
  );
}

/**
 * Chip na tabela (somente ativos com sentido).
 */
export function GoDeviceInstallStatusChip({ deviceState, onOpen, disabled }) {
  const status = deviceState?.status || 'unknown';
  const meta = statusMeta(status);

  return (
    <GoHoverTooltip text={meta.tip} align="center">
      <button
        type="button"
        className={`go-dev-install-chip ${meta.className}`}
        disabled={disabled}
        aria-label={`Instalação: ${meta.label}`}
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.();
        }}
      >
        <span className="go-dev-install-chip-ico" aria-hidden>
          {status === 'ok' ? (
            <Check size={13} strokeWidth={2.6} />
          ) : status === 'attention' ? (
            <CircleAlert size={13} strokeWidth={2.2} />
          ) : (
            <CircleHelp size={13} strokeWidth={2.2} />
          )}
        </span>
        <span className="go-dev-install-chip-label">{meta.label}</span>
      </button>
    </GoHoverTooltip>
  );
}

/**
 * Modal com checklist do go_device_state.
 */
export function GoDeviceInstallStatusModal({ open, device, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open || !device) return null;

  const state = device.deviceState;
  const status = state?.status || 'unknown';
  const meta = statusMeta(status);
  const checks = state?.checks || [];
  const updated = formatWhen(state?.updatedAt);
  const changed = formatWhen(state?.changedAt);
  const titleName = String(device.nomeUsuario || '').trim() || 'Dispositivo';
  const modelLine = [device.brand, device.modelo].filter(Boolean).join(' · ') || '—';

  return ReactDOM.createPortal(
    <div
      className="go-dev-install-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="go-dev-install-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Situação da instalação"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="go-dev-install-modal-head">
          <div className="go-dev-install-modal-top">
            <div className="go-dev-install-modal-kicker">Instalação do aparelho</div>
            <div className="go-dev-install-modal-top-actions">
              <div className={`go-dev-install-modal-badge ${meta.className}`}>
                <span className="go-dev-install-modal-badge-ico" aria-hidden>
                  {status === 'ok' ? (
                    <Check size={13} strokeWidth={2.6} />
                  ) : status === 'attention' ? (
                    <CircleAlert size={13} strokeWidth={2.2} />
                  ) : (
                    <CircleHelp size={13} strokeWidth={2.2} />
                  )}
                </span>
                {meta.label}
              </div>
              <button
                type="button"
                className="go-dev-install-modal-close"
                aria-label="Fechar"
                onClick={onClose}
              >
                <X size={16} strokeWidth={2} aria-hidden />
              </button>
            </div>
          </div>

          <div className="go-dev-install-person">
            <InstallModalAvatar name={titleName} photoUrl={device.profilePhotoUrl || null} />
            <div className="go-dev-install-person-text">
              <h2 className="go-dev-install-modal-title">{titleName}</h2>
              <p className="go-dev-install-modal-sub">{modelLine}</p>
            </div>
          </div>
        </header>

        {!state?.hasReport ? (
          <div className="go-dev-install-empty">
            Este aparelho ainda não enviou o relatório de permissões. Peça ao técnico para abrir o
            app Go na versão mais recente.
          </div>
        ) : (
          <>
            <ul className="go-dev-install-list">
              {checks.map((c) => (
                <li key={c.key} className={`go-dev-install-item is-${c.level}`}>
                  <span className="go-dev-install-item-ico" aria-hidden>
                    <LevelIcon level={c.level} />
                  </span>
                  <div className="go-dev-install-item-body">
                    <div className="go-dev-install-item-row">
                      <span className="go-dev-install-item-label">{c.label}</span>
                      <span className="go-dev-install-item-value">{c.value}</span>
                    </div>
                    {c.hint ? <p className="go-dev-install-item-hint">{c.hint}</p> : null}
                  </div>
                </li>
              ))}
            </ul>
            {(updated || changed) && (
              <footer className="go-dev-install-modal-foot">
                {updated ? <span>Reportado em {updated}</span> : null}
                {changed ? <span>Última mudança em {changed}</span> : null}
              </footer>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
